import React from 'react';
import {Platform, View} from 'react-native';
import {get, noop} from 'lodash';

import Checkbox from '/component/base/Checkbox/Checkbox';
import Icon from '/component/base/Icon/Icon';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity/TouchableOpacity';
import {DownlineProfile} from '/constant/TestIDs';
import {JOURNEY_KICK_OFF_CALL} from '/enum/clientJourneyTasks.enum';
import {SCREEN_NAMES} from '/enum/tealium.enum';
import {useTranslation} from '/provider/TranslationProvider';
import testProperties from '/util/testing/testProperties';

import {
  generateOnCheckboxChange,
  getFormattedString,
} from '../ClientJourneyTask.util';

import {styles} from './TaskItem.style';
import {generateOnPress} from './TaskItem.util';
interface TaskItemType {
  id?: number;
  label?: string;
  checkStatus?: boolean;
  chkBoxCode?: string;
}
interface TaskItemProps {
  taskItem: {item: TaskItemType};
  onTaskStatusChange: (checked: boolean, id?: number) => void;
  iconName: string;
  isChecked: boolean;
  isDisabled: boolean;
  taskAvailabilityTextComponent: React.ReactNode;
  journeyKickOffCallDescComponent: React.ReactNode;
  firstWeekCelebrationDescComponent: React.ReactNode;
  weeklyCheckInsDescComponent: React.ReactNode;
  firstWeekCompleteItem: boolean;
  style: {
    taskItemText?: any;
    taskItem?: any;
  };
  onShowTaskDetail: () => void;
  lastPaidRank: string | number;
}

export const TaskItem: React.FC<TaskItemProps> = ({
  taskItem: {item},
  onTaskStatusChange = noop,
  iconName = null,
  isChecked = false,
  isDisabled = false,
  taskAvailabilityTextComponent = null,
  journeyKickOffCallDescComponent = null,
  firstWeekCelebrationDescComponent = null,
  weeklyCheckInsDescComponent = null,
  firstWeekCompleteItem = false,
  style: styleProp = {},
  onShowTaskDetail = noop,
  lastPaidRank,
}) => {
  const {tt} = useTranslation('screen.clients.section.clientJourney.firstWeek');
  const getFirstWeekCompleteStyleProp = () => {
    if (firstWeekCompleteItem) {
      return {};
    } else if (
      isDisabled ||
      (isChecked && get(item, 'label', '') !== JOURNEY_KICK_OFF_CALL) ||
      isChecked
    ) {
      return {...styles.disabledContainer, ...styles.taskItemContainer};
    } else {
      return {...styles.taskItemContainer, ...styles.enabledContainer};
    }
  };

  const getDisabledIconStyle = () => {
    if (isDisabled) {
      return styles.disabledIcon;
    } else if (item?.checkStatus === true) {
      return styles.selectedIcon;
    } else {
      return styles.unSelectedIcon;
    }
  };

  const getItemLabelStyle = () => {
    if (isChecked) {
      return {
        ...styles.taskItemText,
        ...styles.itemSelectedText,
        ...(styleProp?.taskItemText || {}),
      };
    } else if (isDisabled) {
      return {
        ...styles.taskItemText,
        ...styles.itemDisabledText,
        ...(styleProp?.taskItemText || {}),
      };
    } else {
      return {
        ...styles.taskItemText,
        ...styles.itemUnSelectedText,
        ...(styleProp?.taskItemText || {}),
      };
    }
  };
  const isOptaviaLabel = get(item, 'label', '').includes(
    'Share the OPTAVIA App',
  );
  const shouldRenderSplitOptaviaLabel = isOptaviaLabel && Platform.OS !== 'web';
  const handleCheckboxContainerClick = (event: any) => {
    if (Platform.OS === 'web') {
      event?.stopPropagation?.();
    }
  };
  return (
    <View style={styles.base} {...testProperties(null, true)}>
      {taskAvailabilityTextComponent}
      <TouchableOpacity
        style={getFirstWeekCompleteStyleProp()}
        disabled={isDisabled}
        onPress={generateOnPress({
          item,
          isChecked,
          onTaskStatusChange,
          onShowTaskDetail,
          lastPaidRank,
        })}
        {...testProperties(
          `${DownlineProfile.cjtaskItem}-${isChecked ? '' : 'un'}checked`,
          true,
        )}>
        <View style={{...styles.taskItem, ...(styleProp?.taskItem || {})}}>
          <View
            style={styles.iconContainer}
            // @ts-ignore - onClick is web-specific
            onClick={handleCheckboxContainerClick}>
            <Checkbox
              checkedIconName={iconName}
              checked={isChecked}
              style={{icon: getDisabledIconStyle()}}
              onChange={(e: any) => {
                onTaskStatusChange(e, get(item, 'id'));
                generateOnCheckboxChange({
                  checkboxName: get(item, 'label', ''),
                  currentScreenName: SCREEN_NAMES.profile,
                  checkedStatus: e,
                  lastPaidRank: lastPaidRank,
                });
              }}
              disabled={isDisabled}
              rectangleCheckbox
            />
          </View>
          {!shouldRenderSplitOptaviaLabel ? (
            <Text
              variant="button"
              style={getItemLabelStyle()}
              {...testProperties(DownlineProfile.cjtaskItemLabel)}>
              {tt(getFormattedString(get(item, 'label', '')), `${item?.label}`)}
            </Text>
          ) : (
            <Text
              style={getItemLabelStyle()}
              variant="button"
              {...testProperties(DownlineProfile.cjtaskItemLabel)}>
              Share the OPTA
              <Text
                variant="button"
                style={[getItemLabelStyle(), styles.optaviaText]}>
                {'VIA '}
              </Text>
              App
            </Text>
          )}
          {!firstWeekCompleteItem && (
            <Icon name={'chevron-right'} style={styles.arrowIcon} />
          )}
        </View>
        {journeyKickOffCallDescComponent}
        {!isDisabled && firstWeekCelebrationDescComponent}
        {weeklyCheckInsDescComponent}
      </TouchableOpacity>
    </View>
  );
};
